import { Box, Typography, useTheme } from "@mui/material";
import { LinkItem } from "LinkItem";
import { useReports } from "api/reports";
import { useStores } from "api/stores";
import { SelectInput } from "components/FormControls/Select/Select";
import { LineChartOverview } from "components/GraphsSections/LineChartWithOverview/LineChartOverview";
import {
  formatDateToParse,
  getFormatByAggregationType,
} from "components/GraphsSections/LineChartWithOverview/utils";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";
import {
  AggregationType,
  ChartId,
  ChartTab,
  LineChartData,
  UsageMetrics,
  UsageMetricsUnion,
} from "types/charts";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { useStyles } from "./UsageChart.styles";

export function UsageChart() {
  const theme: OLTheme = useTheme();
  const classes = useStyles();
  const { message } = useMessage();
  const { useInfiniteStoreList } = useStores();
  const { useBillableReport } = useReports();

  const [storeSearch, setStoreSearch] = useState("");
  const [selectedTenant, setSelectedTenant] = useState("all");
  const [activeTab, setActiveTab] = useState<UsageMetricsUnion>(
    UsageMetrics.TRANSACTIONS,
  );

  const { data: reportData } = useBillableReport({
    filters:
      selectedTenant !== "all"
        ? [
            {
              query: "tenantId",
              value: selectedTenant,
              condition: "",
            },
          ]
        : [],
  });

  const {
    data: infiniteStoresList,
    size: storesSize,
    setSize: setStoresSize,
  } = useInfiniteStoreList({
    filters: storeSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: storeSearch,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: storesList, anyElementsToFetch: anyStoresToFetch } =
    useManageInfiniteScroll(infiniteStoresList || []);

  const tenantsItems = [
    { id: "all", label: message({ id: "showFromAllTenants" }) },
    ...storesList.map((store) => ({
      id: store?.storeId,
      label: store?.name,
    })),
  ];

  const chartData =
    activeTab === UsageMetrics.TRANSACTIONS
      ? reportData?.billableTransactionsPerMonth
      : reportData?.billableMembersPerMonth;

  const lineChartData: LineChartData[] = reportData && [
    {
      color: theme.primary500,
      data: Object.keys(chartData).map((key) => {
        const dateToParse = formatDateToParse(key, AggregationType.MONTH);

        return {
          x: Date.parse(dateToParse)
            ? OLDate(dateToParse, {
                formatString: getFormatByAggregationType("month"),
              })
            : "",
          y: Number(chartData[key]),
        };
      }),
      id: ChartId.MAIN,
    },
  ];

  const chartTabs: ChartTab[] = [
    {
      name: UsageMetrics.TRANSACTIONS,
      tooltip: {
        text: message({ id: "transactionsTabTooltipDescription" }),
      },
      value: reportData?.billableTransactions,
      formatNumber: true,
    },
    {
      name: UsageMetrics.BILLABLE_MEMBERS,
      tooltip: {
        text: message({ id: "billableMembersTabTooltipDescription" }),
      },
      value: reportData?.billableMembers,
      formatNumber: true,
    },
  ];

  return (
    <>
      <TopHeader title={message({ id: "globalManagement" })} />
      <Box display="flex" justifyContent="space-between" mt={12}>
        <Typography fontSize={18} mb={10} data-test-id="usage-chart">
          {message({ id: "usageChart" })}
        </Typography>
        <Formik initialValues={{}} onSubmit={() => {}}>
          <Form>
            <SelectInput
              displaySearch
              name="tenant"
              items={tenantsItems}
              data-test-id="tenants"
              className={classes.selectInput}
              placeholder={message({ id: "showFromTenant" })}
              anyElementsToFetch={anyStoresToFetch}
              asyncSearch={(value) => setStoreSearch(value)}
              loadMoreElements={() => setStoresSize(storesSize + 1)}
              onChange={(_, value) => setSelectedTenant(value)}
              searchPlaceholder={`${message({ id: "searchForTenant" })}...`}
            />
          </Form>
        </Formik>
      </Box>
      <Typography className={classes.description}>
        {message(
          { id: "usageChartDescription" },
          {
            link: (
              <LinkItem blue to={links.dashboard}>
                {message({ id: "seeTheDashboard" }).toLocaleLowerCase()}
              </LinkItem>
            ),
          },
        )}
      </Typography>
      <LineChartOverview
        showLegend={false}
        tabs={chartTabs}
        data={lineChartData}
        activeTab={activeTab}
        aggregationType={AggregationType.MONTH}
        setActiveTab={setActiveTab}
        enableDownload
        intervalLabel={message({ id: "last12months" })}
      />
    </>
  );
}
